export type LocalTheme = 'light' | 'dark' | 'system';
export type ResolvedTheme = Exclude<LocalTheme, 'system'>;

export interface DocumentThemeOptions {
  contrast?: 'normal' | 'high';
  themeColor?: string;
}

export function getLocalTheme(storageKey: string): LocalTheme {
  const value = window.localStorage.getItem(storageKey);
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'system';
}

export function resolveTheme(theme: LocalTheme): ResolvedTheme {
  if (theme !== 'system') return theme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyDocumentTheme(
  resolved: ResolvedTheme,
  options: DocumentThemeOptions = {},
): ResolvedTheme {
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.contrast = options.contrast ?? 'normal';
  document.documentElement.style.colorScheme = resolved;
  const themeColor = options.themeColor ?? getComputedStyle(document.documentElement)
    .getPropertyValue('--aw-canvas')
    .trim();
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta && themeColor) meta.content = themeColor;
  window.dispatchEvent(
    new CustomEvent('aw-theme-change', { detail: { resolved, ...options } }),
  );
  return resolved;
}

export function applyLocalTheme(theme: LocalTheme): ResolvedTheme {
  return applyDocumentTheme(resolveTheme(theme));
}

export function saveLocalTheme(storageKey: string, theme: LocalTheme): 'light' | 'dark' {
  window.localStorage.setItem(storageKey, theme);
  return applyLocalTheme(theme);
}
